import {
  Component,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { GalleryImage } from './gallery-image';
import { GalleryService } from './gallery.service';

@Component({
  selector: 'app-gallery-grid',
  standalone: true,
  styleUrl: './gallery-grid.scss',
  templateUrl: './gallery-grid.html',
})
export class GalleryGrid {
  private readonly galleryService = inject(GalleryService);

  // Click behavior for images that carry a linkUrl:
  // - false (default): the thumbnail always opens the lightbox; a
  //   small external-link icon on the thumbnail (and in the
  //   lightbox) opens linkUrl alongside it, in a new tab.
  // - true: clicking the thumbnail opens linkUrl directly instead of
  //   the lightbox.
  protected openExternalLinksDirectly = false;

  protected readonly images = signal<GalleryImage[]>([]);
  protected readonly lightboxOpen = signal(false);
  protected readonly currentIndex = signal(0);

  constructor() {
    this.galleryService
      .getImages()
      .subscribe((images) => this.images.set(images));
  }

  protected onImageClick(image: GalleryImage, index: number): void {
    if (this.openExternalLinksDirectly && image.linkUrl) {
      this.openExternalLink(image.linkUrl);
      return;
    }

    this.openLightbox(index);
  }

  protected onExternalLinkButtonClick(event: Event, url: string): void {
    event.stopPropagation();
    this.openExternalLink(url);
  }

  private openExternalLink(url: string): void {
    window.open(url, '_blank', 'noopener');
  }

  protected openLightbox(index: number): void {
    this.currentIndex.set(index);
    this.lightboxOpen.set(true);
  }

  protected closeLightbox(): void {
    this.lightboxOpen.set(false);
  }

  protected showNext(): void {
    const total = this.images().length;
    this.currentIndex.update((i) => (i + 1) % total);
  }

  protected showPrevious(): void {
    const total = this.images().length;
    this.currentIndex.update((i) => (i - 1 + total) % total);
  }

  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (!this.lightboxOpen()) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        this.closeLightbox();
        break;
      case 'ArrowRight':
        this.showNext();
        break;
      case 'ArrowLeft':
        this.showPrevious();
        break;
    }
  }
}
